import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from "react";

export const SELECT_LIST_MAX_HEIGHT = 480;
export const SELECT_LIST_GAP = 4;

export const useSelectListBox = (
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  listRef: RefObject<HTMLElement | null>,
) => {
  const [listBox, setListBox] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: SELECT_LIST_MAX_HEIGHT,
  });

  const updateListPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const width = rect.width;
    // Small selects can render wider than the trigger to fit their options.
    const renderedWidth = Math.max(width, listRef.current?.offsetWidth ?? 0);
    let left = rect.left;
    if (left + renderedWidth > window.innerWidth - SELECT_LIST_GAP) {
      left = Math.max(
        SELECT_LIST_GAP,
        window.innerWidth - renderedWidth - SELECT_LIST_GAP,
      );
    }
    const spaceBelow = window.innerHeight - rect.bottom - SELECT_LIST_GAP;
    const spaceAbove = rect.top - SELECT_LIST_GAP;
    const measuredHeight =
      listRef.current?.offsetHeight ?? SELECT_LIST_MAX_HEIGHT;
    const openUpward = spaceBelow < measuredHeight && rect.top > spaceBelow;
    const availableHeight = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
      0,
      Math.min(SELECT_LIST_MAX_HEIGHT, availableHeight),
    );
    const listHeight = Math.min(measuredHeight, maxHeight);
    setListBox({
      top: openUpward
        ? Math.max(SELECT_LIST_GAP, rect.top - listHeight - SELECT_LIST_GAP)
        : rect.bottom + SELECT_LIST_GAP,
      left,
      width,
      maxHeight,
    });
  }, [listRef, triggerRef]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updateListPosition();
  }, [open, updateListPosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    window.addEventListener("resize", updateListPosition);
    window.addEventListener("scroll", updateListPosition, true);
    return () => {
      window.removeEventListener("resize", updateListPosition);
      window.removeEventListener("scroll", updateListPosition, true);
    };
  }, [open, updateListPosition]);

  return listBox;
};
